import React, { useEffect, useState, useRef } from "react";
import {
    NEWS_NO_RESULTS_ERROR,
    NEWS_NO_RESULTS_TEXT,
    NEWS_PAGE_SIZE,
    NEWS_RESET_FILTERS_TEXT,
    NEWS_TERM_NEWS_INDUSTRY,
    NEWS_TERM_NEWS_TYPE,
    NEWS_TERM_NEWS_YEAR,
    newsCategoryFieldMapping,
    NEWS_SEARCH_PLACEHOLDER,
} from "@qlik/qlik/src/lib/constants/news-constants";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import ImageCard from "@qlik/qlik/src/components/Card/Single-Card-Image";
import Pagination from "@qlik/qlik/src/components/Common/Pagination";
import { useDebouncedCallback } from "@qlik/qlik/src/hooks/useDebouncedCallback";
import { fetchNewsListing } from "@qlik/qlik/src/lib/services/news/api";
import { newsMapper } from "@qlik/qlik/src/lib/util/mappers/contentful-response-mappers";
import {
    getPagePath,
    getValidatedCategoryFilters,
    scrollToElement,
    getMicrocopyValueFromKeys,
} from "@qlik/qlik/src/lib/util/helpers";
import DropdownFilterGroup from "@qlik/qlik/src/components/Common/DropdownFilterGroup";
import FilterChips from "@qlik/qlik/src/components/Common/FilterChips";
import NewsCategoryData from "@qlik/qlik/src/graphql/queries/news";
import { toYear } from "@qlik/qlik/src/lib/util/dates";
import useUrlQueryParamsState from "@qlik/qlik/src/hooks/useUrlQueryParamsState";
import { useI18n } from "@qlik/utils/i18nContext";
import useSiteMetadata from "@qlik/qlik/src/hooks/useSiteMetaData";

const NewsListing = ({ data }) => {
    const { additionalConfiguration, backgroundColor } = data || {};
    const [listing, setListing] = useState([]); // filtered items
    const [total, setTotal] = useState(0); // total items available for the current query
    const [categories, setCategories] = useState([]); // set of filter categories - static
    const [isLoading, setIsLoading] = useState(true);
    const [currentCategory, setCurrentCategory] = useState(""); // keeps track of the last filtered category

    const { i18n } = useI18n();

    // The contentful env and preview status used for this build, to be passed to the Netlify function so that we query resources for the current environment.
    const { env, preview } = useSiteMetadata();

    const initialFilters = {
        page: 0,
        limit: NEWS_PAGE_SIZE,
        query: "",
        [NEWS_TERM_NEWS_INDUSTRY]: [],
        [NEWS_TERM_NEWS_TYPE]: getPagePath(), // render specific news type on page - static filter
        [NEWS_TERM_NEWS_YEAR]: [],
    };

    const [filters, setFilters] = useUrlQueryParamsState(initialFilters, newsCategoryFieldMapping);

    const { allCategoryData } = NewsCategoryData();

    // set initial news type filter by checking the page slug
    useEffect(() => {
        const pagePath = getPagePath(window);
        setFilters((prev) => ({ ...prev, [NEWS_TERM_NEWS_TYPE]: pagePath }));
    }, [setFilters]);

    // set category filters with validations
    useEffect(() => {
        const pagePath = getPagePath(window);

        const filteredData = allCategoryData?.By_Year.filter((item) =>
            item.edges.some((edge) => edge.node.newsType?.title === pagePath)
        );

        const updatedCategoryData = { ...allCategoryData, By_Year: filteredData };

        // construct category objects
        const newsCategoryData = Object.entries(updatedCategoryData).map(([categoryName, categoryFilters]) => {
            const transformedCategoryName = categoryName.replace("_", " ");
            const categoryObj = {
                id: transformedCategoryName,
                title: transformedCategoryName,
                subTerms: [],
            };

            // construct category filter items for each category
            categoryObj.subTerms = categoryFilters.map(({ fieldValue, edges }) => {
                // convert date string to year
                const value = transformedCategoryName === NEWS_TERM_NEWS_YEAR ? toYear(fieldValue) : fieldValue;

                const subTermObj = {
                    id: value,
                    title: value,
                    isDisabled: false,
                    items: edges.map((item) => item.node.id), // keep the resource items for each category to update filters availability
                };

                return subTermObj;
            });

            // set distinct category values only
            categoryObj.subTerms = categoryObj.subTerms.reduce(
                (prev, current) => (prev.some((subTerm) => subTerm.id === current.id) ? prev : [...prev, current]),
                []
            );

            // sort category filters in desc
            categoryObj.subTerms.sort((a, b) => b.title.toLowerCase().localeCompare(a.title.toLowerCase()));

            return categoryObj;
        });

        const validatedCategories = getValidatedCategoryFilters(
            { title: currentCategory, value: filters[currentCategory] },
            newsCategoryData
        );
        setCategories(validatedCategories);
    }, [filters, currentCategory, allCategoryData]);

    const [
        noResultsText,
        clearFiltersText,
        noResultsValidationText,
        searchPlaceholderText,
    ] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        NEWS_NO_RESULTS_TEXT,
        NEWS_RESET_FILTERS_TEXT,
        NEWS_NO_RESULTS_ERROR,
        NEWS_SEARCH_PLACEHOLDER,
    ]);

    // create a debounced version of API call for fetching news entries
    // only state dependancy is filters state
    const fetchNews = useDebouncedCallback(async () => {
        try {
            const results = await fetchNewsListing(
                filters,
                env,
                i18n.curr.enLangName,
                i18n.curr.contentfulCode,
                preview
            );
            if (!results) throw new Error(noResultsValidationText);
            setTotal(results.results?.total);
            setListing(newsMapper(results.results?.items));
            setIsLoading(false);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    });

    // fetch news listing as per the filters applied
    // fetch category information of each item for current filters
    useEffect(() => {
        const updateListing = async () => {
            setIsLoading(true);
            await fetchNews();
        };

        updateListing();
    }, [filters, fetchNews]);

    // Handle search query updates
    const handleQueryChange = (event) => {
        event.preventDefault();
        const query = event?.target?.value;
        // contentful will only return results for queries more than 2 characters long
        setFilters((prev) => ({ ...prev, page: 0, query }));
    };

    const sectionRef = useRef(null);
    const handlePageChange = (selectedPage) => {
        setFilters((prev) => ({ ...prev, page: selectedPage }));
        scrollToElement(sectionRef);
    };

    const {
        sectionColorMapping: { bgColor },
    } = getColorClassName(backgroundColor);
    const paddingClassName = getSpacingClassName(additionalConfiguration);

    // filters which are reflected in filter chips
    // extract only needed filters from all filters for chips
    const filterChipFilters = {
        query: filters.query,
        [NEWS_TERM_NEWS_INDUSTRY]: filters[NEWS_TERM_NEWS_INDUSTRY],
        [NEWS_TERM_NEWS_YEAR]: filters[NEWS_TERM_NEWS_YEAR],
    };

    // update filter state when filter chip close
    const handleFilterChipClose = (filterCategory, filterValue) => {
        const currentFilterValue = filters[filterCategory];
        let updatedFilter = "";
        if (typeof currentFilterValue !== "string") {
            updatedFilter = currentFilterValue?.filter((filter) => filter !== filterValue);
        }
        setCurrentCategory(filterCategory);
        setFilters((prev) => ({ ...prev, [filterCategory]: updatedFilter }));
    };

    const resetFilters = () => {
        setCurrentCategory("");
        setFilters((prev) => ({
            ...prev,
            page: 0,
            query: "",
            [NEWS_TERM_NEWS_INDUSTRY]: [],
            [NEWS_TERM_NEWS_YEAR]: [],
        }));
    };

    // map listing items with necessary adjustments for this page
    const mappedItems = listing?.map((singleItem) => {
        const mappedItem = { ...singleItem };
        delete mappedItem.subtitle;
        delete mappedItem.featuredImage;
        delete mappedItem.image;
        return mappedItem;
    });

    return (
        <section ref={sectionRef} className={`${paddingClassName} ${bgColor} scroll-m-20`} id={data?.customId}>
            <div className="container">
                <div>
                    <div className="grid md:grid-cols-3 gap-7.5 my-5">
                        <input
                            className="body w-full h-15 py-4 pl-15 pr-5 bg-transparent border border-stone bg-filterSearch bg-no-repeat bg-filter-search-icon rounded placeholder:text-gray"
                            type="text"
                            placeholder={searchPlaceholderText}
                            value={filters.query}
                            onChange={handleQueryChange}
                        />
                        {categories.map((category) =>
                            category.subTerms?.length ? (
                                <DropdownFilterGroup
                                    key={category.id}
                                    filterName={category.title}
                                    parentCategory={category.title}
                                    categoryData={category.subTerms}
                                    allParams={filters}
                                    setAllParams={setFilters}
                                    setCurrentCategory={setCurrentCategory}
                                />
                            ) : null
                        )}
                    </div>
                    {Object.values(filterChipFilters)
                        .flat()
                        .filter(Boolean).length > 0 && (
                        <div className="flex flex-wrap gap-2.5">
                            <FilterChips
                                filters={filterChipFilters}
                                onClose={handleFilterChipClose}
                                onResetFilters={resetFilters}
                                resetFiltersLabel={clearFiltersText}
                            />
                        </div>
                    )}
                </div>
                <div>
                    {!isLoading && mappedItems && mappedItems.length === 0 && (
                        <p className="text-center font-semibold text-stone mt-15">{noResultsText}</p>
                    )}
                    <div
                        className={
                            // (isLoading ? "blur-sm " : "") +
                            "transition grid md:grid-cols-2 lg:grid-cols-3 gap-7.5 lg:gap-y-15 lg:mt-15"
                        }
                    >
                        {mappedItems?.map((item) => (
                            <ImageCard key={item.id} data={item} additionalClasses="min-w-full" />
                        ))}
                    </div>
                </div>
                <div>
                    <Pagination
                        currentPage={filters.page}
                        limit={filters.limit}
                        total={total}
                        handlePageChange={handlePageChange}
                    />
                </div>
            </div>
        </section>
    );
};

export default NewsListing;
